<template>
	<div
		class="relative size-full md:h-[95vh] md:max-w-[95%]"
		@dblclick.prevent="closeZoom"
		@touchend="handleTouchEnd"
		@touchmove.prevent
	>
		<FloatingButton
			container-class="absolute left-1/2 bottom-8 transform -translate-x-1/2"
			@click="closeZoom"
		>
			<IconMaterialSymbolsLightZoomOutRounded class="size-6" />
		</FloatingButton>
		<img
			ref="imageRef"
			v-bind="imageProps"
			class="size-full object-cover transition-transform duration-200 md:h-auto md:w-full"
			@load="onImageLoad"
		/>
	</div>
</template>

<script setup lang="ts">
import type { ZoomableImageProps } from '.';
import 'iv-viewer/dist/iv-viewer.css';
import ImageViewer from 'iv-viewer';
import FloatingButton from '../floating-button/FloatingButton.vue';

const props = defineProps<ZoomableImageProps>();

const emits = defineEmits<{
	(event: 'close'): void;
}>();

//Utilizando a imagem com a tag <img> devido a problemas de compatibilidade do NuxtImg com o iv-viewer
const imageRef = ref<HTMLImageElement | null>(null);

let viewer: ImageViewer | null = null;

function closeZoom() {
	emits('close');
}

const onImageLoad = () => {
	if (!imageRef.value) return;
	viewer = new ImageViewer(imageRef.value, {
		zoomOnMouseWheel: true,
		snapView: false
	});

	if (props.imageProps?.src) {
		viewer.load(props.imageProps.src);
	}

	const imageElement = viewer['_elements'].image as HTMLImageElement | null;

	if (imageElement) {
		const imageWidth = imageElement.clientWidth;
		const imageHeight = imageElement.clientHeight;

		// Converter a porcentagem recebida em coordenadas
		const zoomPositionX = (props.zoomPosition.x / 100) * imageWidth;
		const zoomPositionY = (props.zoomPosition.y / 100) * imageHeight;

		viewer.zoom(150, { x: zoomPositionX, y: zoomPositionY });
	}
};

const DOUBLE_TAP_THRESHOLD = 300;
let lastTapTimestamp = 0;

/**
 * Valida o fim do gesto de toque na tela, diferenciando entre um movimento de pinça e um duplo toque.
 * - Se todos os toques forem liberados e apenas um toque foi finalizado, verifica se é um duplo toque.
 * - Permite o duplo toque para fechar o zoom, enquanto ignora gestos de pinça (multitoque).
 * - Previne o comportamento padrão do navegador em caso de duplo toque (como zoom automático).
 */
function handleTouchEnd(event: TouchEvent) {
	const noActiveTouches = event.touches.length === 0;
	const singleTouchEnded = event.changedTouches.length === 1; // Apenas um toque foi finalizado

	if (noActiveTouches && singleTouchEnded) {
		if (isDoubleTap()) {
			closeZoom();
			event.preventDefault();
		}
	}
}

function isDoubleTap(): boolean {
	const currentTimestamp = performance.now();
	const timeDifference = currentTimestamp - lastTapTimestamp;
	lastTapTimestamp = currentTimestamp;
	return timeDifference < DOUBLE_TAP_THRESHOLD;
}
</script>

<style lang="scss" scoped>
::v-deep(.iv-container) {
	@apply flex size-full items-center justify-center;
}

::v-deep(.iv-image, .iv-image-wrap) {
	@apply size-full object-cover;
}
</style>
